import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { motionDuration, motionEase } from '@/features/ui/motion/motion-tokens';
import { ToastContext, type ToastInput, type ToastTone } from './toast-context';
import './toast.css';

type ToastItem = ToastInput & {
  id: string;
  tone: ToastTone;
};

const DEFAULT_DURATION = 4000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const reduceMotion = useReducedMotion();

  const dismiss = useCallback((id: string) => {
    const t = timers.current.get(id);
    if (t) {
      clearTimeout(t);
      timers.current.delete(id);
    }
    setItems((prev) => prev.filter((x) => x.id !== id));
  }, []);

  const scheduleDismiss = useCallback(
    (id: string, durationMs: number) => {
      const existing = timers.current.get(id);
      if (existing) clearTimeout(existing);
      const t = setTimeout(() => dismiss(id), durationMs);
      timers.current.set(id, t);
    },
    [dismiss],
  );

  const push = useCallback(
    (input: ToastInput) => {
      const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const tone = input.tone ?? 'info';
      const durationMs = input.durationMs ?? DEFAULT_DURATION;
      setItems((prev) => [...prev, { ...input, id, tone }]);
      if (durationMs > 0) scheduleDismiss(id, durationMs);
    },
    [scheduleDismiss],
  );

  const api = useMemo(
    () => ({
      push,
      success: (message: string, title?: string) =>
        push({ message, title: title ?? 'Succès', tone: 'success' as const }),
      error: (message: string, title?: string) =>
        push({ message, title: title ?? 'Erreur', tone: 'error' as const }),
      info: (message: string, title?: string) => push({ message, title, tone: 'info' as const }),
      dismiss,
    }),
    [push, dismiss],
  );

  const pause = (id: string) => {
    const t = timers.current.get(id);
    if (t) {
      clearTimeout(t);
      timers.current.delete(id);
    }
  };

  const resume = (id: string, durationMs = DEFAULT_DURATION) => {
    scheduleDismiss(id, durationMs);
  };

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="toast-viewport" aria-live="polite" aria-relevant="additions">
        <AnimatePresence initial={false}>
          {items.map((item) => (
            <motion.div
              key={item.id}
              className={`toast toast--${item.tone}`}
              role={item.tone === 'error' ? 'alert' : 'status'}
              initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -10, scale: 0.98 }}
              animate={reduceMotion ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }}
              exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -6, scale: 0.98 }}
              transition={{ duration: motionDuration.base, ease: motionEase }}
              onMouseEnter={() => pause(item.id)}
              onMouseLeave={() => resume(item.id, item.durationMs ?? DEFAULT_DURATION)}
            >
              <div className="toast-message">
                {item.title ? <p className="toast-title">{item.title}</p> : null}
                <p style={{ margin: 0 }}>{item.message}</p>
              </div>
              <button
                type="button"
                className="toast-dismiss"
                aria-label="Fermer"
                onClick={() => dismiss(item.id)}
              >
                ×
              </button>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
}
